import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api";
import "./MemberList.css";

const getLoggedInUser = () => {
  try {
    const raw = localStorage.getItem("loggedInUser");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const getToken = () => {
  try {
    return localStorage.getItem("token") || "";
  } catch {
    return "";
  }
};

const MemberList = ({ user }) => {
  const navigate = useNavigate();

  const [currentUser, setCurrentUser] = useState(user || null);
  const [token, setToken] = useState("");
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");

  // Guard: only allow logged-in + approved alumni
  useEffect(() => {
    const current = currentUser || getLoggedInUser();
    const currentToken = getToken();

    if (!current || !currentToken) {
      setError("Please log in to view the member list.");
      setTimeout(() => navigate("/login", { replace: true }), 2500);
      return;
    }

    if (String(current.status).toLowerCase() !== "approved") {
      setError("Access denied. Only approved alumni can view the member directory.");
      setTimeout(() => navigate("/dashboard", { replace: true }), 3500);
      return;
    }

    setCurrentUser(current);
    setToken(currentToken);
  }, [currentUser, navigate]);

  // Fetch approved members via protected API endpoint
  useEffect(() => {
    const fetchMembers = async () => {
      if (!token) return;
      setLoading(true);
      setError("");

      try {
        const res = await api.get("/api/auth/approved", {
          headers: { Authorization: `Bearer ${token}` },
        });

        const list = Array.isArray(res.data)
          ? res.data
          : [];

        setMembers(list);
      } catch (err) {
        console.error("Failed to fetch members:", err);
        const msg =
          err?.response?.data?.message ||
          "Unable to load members. Please try again later.";
        setError(msg);
      } finally {
        setLoading(false);
      }
    };

    fetchMembers();
  }, [token]);

  // Filter members by query (name, email, course, batch, degree)
  const filteredMembers = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return members;

    return members.filter((m) => {
      const name = String(m.name || "").toLowerCase();
      const email = String(m.email || "").toLowerCase();
      const course = String(m.course || "").toLowerCase();
      const batch = String(m.batchYear || "").toLowerCase();
      const degree = String(m.degree || "").toLowerCase();

      return (
        name.includes(q) ||
        email.includes(q) ||
        course.includes(q) ||
        batch.includes(q) ||
        degree.includes(q)
      );
    });
  }, [members, query]);

  const formatEducation = (m) => {
    const parts = [];
    if (m.degree) parts.push(m.degree);
    if (m.course) parts.push(m.course);
    if (m.college) parts.push(m.college);
    if (m.batchYear) parts.push(`Batch ${m.batchYear}`);
    if (m.studentId) parts.push(`Reg# ${m.studentId}`);
    return parts.join(" • ");
  };

  const formatWorking = (m) => {
    if (!m.isWorking) return "Not working";
    const parts = [];
    if (m.jobTitle) parts.push(m.jobTitle);
    if (m.companyName) parts.push(`@ ${m.companyName}`);
    if (m.industry) parts.push(m.industry);
    if (m.workExperience) parts.push(`${m.workExperience} exp`);
    if (m.workLocation) parts.push(m.workLocation);
    if (m.workMobile) parts.push(m.workMobile);
    if (m.linkedin) parts.push("LinkedIn");
    return parts.join(" • ");
  };

  if (!currentUser) {
    return (
      <div className="memberlist-page">
        <div className="memberlist-shell">
          <div className="memberlist-card">
            <h2>Access Restricted</h2>
            <p>Please log in to view the member list.</p>
            <button className="btn-primary" onClick={() => navigate("/login")}>
              Go to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="memberlist-page">
      <div className="memberlist-shell">
        <header className="memberlist-header">
          <div className="header-left">
            <h1>Alumni Member Directory</h1>
            <p className="subtitle">View-only directory of approved alumni members</p>
          </div>
          <div className="header-right">
            <div className="search-wrap">
              <input
                type="text"
                className="search-input"
                placeholder="Search by name, email, course, batch, degree..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                aria-label="Search members"
              />
            </div>
            <div className="user-chip" title={`Logged in as ${currentUser.name}`}>
              <span className="user-avatar" aria-hidden="true">👤</span>
              <span className="user-name">{currentUser.name}</span>
              <span className="user-status">{currentUser.status}</span>
            </div>
          </div>
        </header>

        <section className="memberlist-card">
          <div className="stats-row">
            <div className="stat">
              <div className="stat-value">{members.length}</div>
              <div className="stat-label">Total Members</div>
            </div>
            <div className="stat">
              <div className="stat-value">{filteredMembers.length}</div>
              <div className="stat-label">Matching</div>
            </div>
          </div>

          {error && <div className="error-banner">{error}</div>}

          {loading ? (
            <div className="loading-wrap">
              <div className="spinner" />
              <p>Loading members...</p>
            </div>
          ) : (
            <>
              <div className="table-wrap">
                <table className="member-table" role="table" aria-label="Alumni members">
                  <thead>
                    <tr>
                      <th scope="col">Name</th>
                      <th scope="col">Email</th>
                      <th scope="col">City</th>
                      <th scope="col">Course</th>
                      <th scope="col">Degree</th>
                      <th scope="col">Batch</th>
                      <th scope="col">Education details</th>
                      <th scope="col">Working details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredMembers.length === 0 ? (
                      <tr>
                        <td className="empty-cell" colSpan={8}>
                          {query
                            ? "No members match your search."
                            : "No approved members found yet."}
                        </td>
                      </tr>
                    ) : (
                      filteredMembers.map((m) => (
                        <tr key={m._id || m.id || `${m.email}-${m.batchYear}`}>
                          <td data-label="Name">{m.name || "-"}</td>
                          <td data-label="Email">{m.email || "-"}</td>
                          <td data-label="City">{m.city || "-"}</td>
                          <td data-label="Course">{m.course || "-"}</td>
                          <td data-label="Degree">{m.degree || "-"}</td>
                          <td data-label="Batch">{m.batchYear || "-"}</td>
                          <td data-label="Education details">{formatEducation(m)}</td>
                          <td data-label="Working details">{formatWorking(m)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <div className="footer-actions">
                <button className="btn-secondary" onClick={() => navigate("/dashboard")}>
                  Back to Dashboard
                </button>
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
};

export default MemberList;